import React, { useMemo, useRef } from 'react';

import useOrganisationSettings from 'components/hooks/useOrganisationSettings';
import { GoalsObjectiveGenericTreeViewTable } from 'screens/Team/Rabbit/Goals/AgGridTreeView/GoalsObjectiveGenericTreeViewTable';
import { ErrorMessage, Loader } from 'screens/analytics/commonComponents';
import useReviewFormParams from 'screens/performanceReview/hooks/useReviewFormParams';

import NoDataImg from 'assets/assetsV1/illustrations/noGoals.svg';

import { ANALYTICS_TEXTS, MODULE_KEYS_USER_PREFERENCE_PERSISTENCE } from 'constants/ProjectConstants';
import { StringModifier, modifyString } from 'constants/copyHelpers';
import { getPersistenceKey } from 'constants/helper';

import { EmptyScreen } from 'uiToolkitV1/EmptyScreen';
import { Paper } from 'uiToolkitV1/Paper';

import { useInvalidateReviewPeriodSummaryOkrs, useReviewPeriodSummaryOkrs } from './dataHooks';

const ReviewPeriodSummaryGoals = () => {
  const { moduleName } = useOrganisationSettings();

  const { employeeId, reviewId } = useReviewFormParams();

  const { data, isError, isLoading } = useReviewPeriodSummaryOkrs({ reviewId, employeeId });
  const dataInvalidator = useInvalidateReviewPeriodSummaryOkrs({ reviewId, employeeId });

  const gridRef = useRef();

  /* Grid State persistence Key */
  const gridStatePersistenceKey = useMemo(() => {
    const viewId = null;
    const tabId = null;
    const uniqueId = 'reviewPeriodSummaryGrid';

    return getPersistenceKey({
      moduleName: MODULE_KEYS_USER_PREFERENCE_PERSISTENCE.REVIEW,
      viewId,
      tabId,
      uniqueId,
    });
  }, []);

  return (
    <Paper p={3}>
      {isLoading && <Loader />}
      {isError && <ErrorMessage errorText={ANALYTICS_TEXTS.REVIEW_TABLE.ERROR_MSG} />}
      {!isLoading &&
        !isError &&
        (data?.length === 0 ? (
          <div style={{ paddingTop: '60px', paddingBottom: '60px' }}>
            <EmptyScreen
              heading={`You haven't created ${modifyString(
                moduleName,
                StringModifier.LOWERCASE_ARTICLE
              )} ${modifyString(moduleName, StringModifier.SENTENCE_CASE)} yet`}
              description={`In the beginners mind, there are only possibilities. Create ${modifyString(
                moduleName,
                StringModifier.LOWERCASE_ARTICLE
              )} ${modifyString(moduleName, StringModifier.SENTENCE_CASE)}, make possibilities real.`}
              illustrationSrc={NoDataImg}
            />
          </div>
        ) : (
          <GoalsObjectiveGenericTreeViewTable
            gridRef={gridRef}
            gridStatePersistenceKey={gridStatePersistenceKey}
            data={data}
            showReviewWeightageColumn
            refetchHandler={() => {
              dataInvalidator();
            }}
            source="ReviewPeriodSummary"
            isReview
          />
        ))}
    </Paper>
  );
};

export default ReviewPeriodSummaryGoals;
